import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { adminProductApi } from '../../../../api/adminProductApi';
import { fetchCurrencyRates } from '../../../../api/settingsApi';
import { extractApiArray } from '../../../../utils/apiResponse';
import { checkIsAdminLoggined } from '../../../../utils/utils';
import useAdminProductList from '../useAdminProductList';
import useMobileResource from './useMobileResource';
import useMobileProductSelection from './useMobileProductSelection';
import MobileProductsToolbar from './MobileProductsToolbar';
import MobileProductsLayout from './MobileProductsLayout';
import MobileProductFilters, { mobileFilterCount } from './MobileProductFilters';
import MobileProductsList from './MobileProductsList';
import MobileProductsPagination from './MobileProductsPagination';
import MobileProductSelection from './MobileProductSelection';
import MobileProductEditor from './MobileProductEditor';
import MobileProductReceipt from './MobileProductReceipt';
import MobileStockHistory from './MobileStockHistory';
import MobileBulkActions from './MobileBulkActions';
import MobileBulkCreateProducts from './MobileBulkCreateProducts';
import MobileDeactivateProduct from './MobileDeactivateProduct';
import MobileCurrencyRates from './MobileCurrencyRates';
import MobileProductFeedback from './MobileProductFeedback';
import MobileProductDialog from './MobileProductDialog';
import '../../../../styles/mobile-admin-products.css';

export default function MobileAdminProductsPage() {
  const history = useHistory();
  const list = useAdminProductList();
  const categories = useMobileResource(
    () => adminProductApi.getCategories().then(extractApiArray),
    'categories',
  );
  const discounts = useMobileResource(
    () =>
      adminProductApi.getDiscountCategories().then(response =>
        extractApiArray(response)
          .map(item => ({ name: typeof item === 'string' ? item : item.name }))
          .filter(item => item.name),
      ),
    'discounts',
  );
  const rates = useMobileResource(fetchCurrencyRates, 'rates', {});
  const selection = useMobileProductSelection(list.products);
  const [dialog, setDialog] = useState(null);
  const [message, setMessage] = useState('');
  useEffect(() => {
    checkIsAdminLoggined({ history });
  }, [history]);
  const close = () => setDialog(null);
  const open = (type, product) => {
    setMessage('');
    setDialog({ type, product });
  };
  const saved = text => {
    if (text) setMessage(text);
    list.refresh();
  };
  const resourcesError = categories.error || discounts.error || rates.error;
  const retryResources = () => {
    categories.reload();
    discounts.reload();
    rates.reload();
  };
  const resources = {
    categories: categories.data,
    discountCategories: discounts.data,
    rates: rates.data,
    resourcesError,
    onRetry: retryResources,
  };
  return (
    <MobileProductsLayout>
      <main className='mp-page'>
        <MobileProductsToolbar
          query={list.query}
          onQuickFilter={list.updateQuery}
          onReset={() => history.push('/admin/products')}
          onRefresh={list.refresh}
          totalCount={list.totalCount}
          loading={list.loading}
          refreshing={list.refreshing}
          filterCount={mobileFilterCount(list.query)}
          onSearch={search => list.updateQuery({ search })}
          onFilters={() => open('filters')}
          onCreate={() => open('edit', {})}
          onBulkCreate={() => open('create')}
          onRates={() => open('rates')}
        />
        <MobileProductFeedback message={message} />
        <MobileProductSelection
          selection={selection}
          loading={list.loading}
          totalCount={list.totalCount}
          onActions={() => setDialog({ type: 'bulk', ids: [...selection.ids] })}
        />
        <MobileProductsList
          products={list.products}
          loading={list.loading}
          refreshing={list.refreshing}
          error={list.error}
          onRetry={list.refresh}
          selectedIds={selection.ids}
          onSelect={selection.toggle}
          onEdit={product => open('edit', product)}
          onReceipt={product => open('receipt', product)}
          onHistory={product => open('history', product)}
          onDeactivate={product => open('deactivate', product)}
        />
        <MobileProductsPagination
          page={list.query.page}
          pageCount={list.pageCount}
          loading={list.loading}
          onChange={page => {
            list.updateQuery({ page }, false);
            document.querySelector('.mp-toolbar')?.scrollIntoView?.({ block: 'start' });
          }}
        />

        {dialog?.type === 'filters' && (
          <MobileProductFilters
            {...resources}
            query={list.query}
            onClose={close}
            onApply={filters => {
              list.updateQuery(filters);
              close();
            }}
          />
        )}
        {dialog?.type === 'edit' && (
          <MobileProductEditor
            {...resources}
            product={dialog.product}
            onSaved={saved}
            onClose={close}
          />
        )}
        {dialog?.type === 'receipt' && (
          <MobileProductReceipt product={dialog.product} onSaved={saved} onClose={close} />
        )}
        {dialog?.type === 'history' && (
          <MobileStockHistory product={dialog.product} onClose={close} />
        )}
        {dialog?.type === 'bulk' && (
          <MobileBulkActions {...resources} ids={dialog.ids} onSaved={saved} onClose={close} />
        )}
        {dialog?.type === 'create' && (
          <MobileBulkCreateProducts {...resources} onSaved={saved} onClose={close} />
        )}
        {dialog?.type === 'deactivate' && (
          <MobileDeactivateProduct product={dialog.product} onSaved={saved} onClose={close} />
        )}
        {dialog?.type === 'rates' && rates.loading && (
          <MobileProductDialog title='Курси валют' onClose={close}>
            <MobileProductFeedback loading />
          </MobileProductDialog>
        )}
        {dialog?.type === 'rates' && !rates.loading && (
          <MobileCurrencyRates
            rates={rates.data}
            error={rates.error}
            onRetry={rates.reload}
            onSaved={text => {
              saved(text);
              rates.reload();
            }}
            onClose={close}
          />
        )}
      </main>
    </MobileProductsLayout>
  );
}
